import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Printer, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";

/**
 * Printable roster snapshot.
 * Query params:
 *   ?position=QB     — filter to one position
 *   ?autoprint=0     — skip auto-print (default: auto-prints on load)
 */
export default function RosterPrint() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const positionFilter = sp.get("position") || "";
  const viewMode = sp.get("view") === "depth" ? "depth" : "list";
  const autoprint = sp.get("autoprint") !== "0";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/roster");
        setData(data);
      } catch (e) {
        setErr("Could not load roster.");
      }
    })();
  }, []);

  useEffect(() => {
    if (data && autoprint) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [data, autoprint]);

  const players = useMemo(() => {
    if (!data?.players) return [];
    let list = data.players;
    if (positionFilter) list = list.filter((p) => p.position === positionFilter);
    // Sort by position then last name for a clean printout
    return [...list].sort((a, b) => {
      const pa = (a.position || "").localeCompare(b.position || "");
      if (pa !== 0) return pa;
      return (a.last_name || "").localeCompare(b.last_name || "");
    });
  }, [data, positionFilter]);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of players) {
      const key = p.position || "—";
      (g[key] = g[key] || []).push(p);
    }
    return g;
  }, [players]);

  if (err) return <div className="p-8 text-red-400">{err}</div>;
  if (!data) return <div className="p-8 text-slate-400">Loading…</div>;

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div data-testid="roster-print-page">
      {/* Print styles — hide app chrome, force white background, tighten table */}
      <style>{`
        @media print {
          aside, [data-testid="topbar"], [data-testid="roster-print-toolbar"] { display: none !important; }
          body, html, .App { background: #fff !important; }
          .print-root { padding: 0 !important; margin: 0 !important; color: #000 !important; }
          .print-root a { color: #000 !important; text-decoration: none !important; }
          .print-root .pos-header { background: #f3f3f3 !important; }
          .print-root table { page-break-inside: auto; }
          .print-root tr { page-break-inside: avoid; page-break-after: auto; }
          .print-root .page-break { page-break-before: always; }
        }
        @page { margin: 0.5in; }
      `}</style>

      {/* On-screen toolbar — hidden when printing */}
      <div className="bg-surface border-b border-surface-3 p-4 flex items-center gap-3 sticky top-0 z-10" data-testid="roster-print-toolbar">
        <button
          onClick={() => nav("/roster")}
          className="flex items-center gap-1 text-xs font-condensed uppercase tracking-widest text-slate-400 hover:text-amber"
          data-testid="print-back"
        >
          <ArrowLeft className="w-3 h-3" /> Back to Roster
        </button>
        <div className="flex-1" />
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-amber hover:bg-amber-600 text-[#0B0D12] px-4 py-2 text-[11px] font-condensed uppercase tracking-widest"
          data-testid="print-btn"
        >
          <Printer className="w-3 h-3" /> Print
        </button>
      </div>

      <div className="print-root max-w-5xl mx-auto bg-white text-black p-10">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-black pb-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
              {viewMode === "depth" ? "Depth Chart" : "Team Roster"}
            </div>
            <div className="text-4xl font-bold uppercase mt-1">Gridiron OS · {user?.program_name || "Your Program"}</div>
            {positionFilter && (
              <div className="text-lg mt-2 uppercase tracking-wider">Position Group: {positionFilter}</div>
            )}
          </div>
          <div className="text-right text-xs text-gray-600">
            <div>Printed: {today}</div>
            <div>{players.length} player{players.length !== 1 ? "s" : ""}</div>
            {data?.config?.last_synced_at && (
              <div className="mt-1">
                Last sync: {new Date(data.config.last_synced_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {players.length === 0 ? (
          <div className="text-gray-500 italic py-8 text-center">
            {positionFilter ? `No ${positionFilter} players in the roster.` : "No players in the roster yet."}
          </div>
        ) : viewMode === "depth" ? (
          <PrintDepthChart players={players} coaches={data?.position_coaches_resolved || {}} />
        ) : positionFilter ? (
          <>
            {(() => {
              const coach = (data?.position_coaches_resolved || {})[positionFilter];
              return coach ? (
                <div className="text-xs text-gray-700 mb-2">
                  <span className="uppercase tracking-widest">Position Coach:</span>{" "}
                  <span className="font-semibold">{coach.name}</span>
                </div>
              ) : null;
            })()}
            <RosterTable rows={players} />
          </>
        ) : (
          Object.keys(grouped).map((pos) => {
            const coach = (data?.position_coaches_resolved || {})[pos];
            return (
              <div key={pos} className="mb-6">
                <div className="pos-header text-sm font-bold uppercase tracking-[0.2em] bg-gray-100 border border-gray-300 px-3 py-2 flex items-center justify-between">
                  <span>{pos} <span className="text-gray-500 font-normal">— {grouped[pos].length}</span></span>
                  {coach && (
                    <span className="text-xs font-normal normal-case tracking-normal text-gray-700">
                      Coach: <span className="font-semibold">{coach.name}</span>
                    </span>
                  )}
                </div>
                <RosterTable rows={grouped[pos]} />
              </div>
            );
          })
        )}

        {/* Legend footer */}
        <div className="mt-8 pt-4 border-t border-gray-300 text-[10px] text-gray-600 flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> = Yes</div>
          <div className="flex items-center gap-1"><XCircle className="w-3 h-3" /> = No</div>
          <div>AD = Athletic Director eligibility clearance · PLAY = Head coach good-to-play</div>
          <div className="flex-1 text-right">gridiron-os.com</div>
        </div>
      </div>
    </div>
  );
}

function RosterTable({ rows }) {
  return (
    <table className="w-full text-xs border-collapse mt-1">
      <thead>
        <tr className="border-b-2 border-black">
          <th className="text-left py-1 px-1">#</th>
          <th className="text-left py-1 px-1">Player</th>
          <th className="text-left py-1 px-1">Pos</th>
          <th className="text-center py-1 px-1">AD</th>
          <th className="text-center py-1 px-1">Play</th>
          <th className="text-left py-1 px-1">Cell</th>
          <th className="text-left py-1 px-1">Guardian</th>
          <th className="text-left py-1 px-1">Guardian Phone</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={p.id} className="border-b border-gray-300">
            <td className="py-1 px-1 text-gray-500">{i + 1}</td>
            <td className="py-1 px-1 font-semibold">{p.last_name}, {p.first_name}</td>
            <td className="py-1 px-1">{p.position || "—"}</td>
            <td className="py-1 px-1 text-center">{p.ad_cleared ? "✓" : "✗"}</td>
            <td className="py-1 px-1 text-center">{p.coach_cleared ? "✓" : "✗"}</td>
            <td className="py-1 px-1 whitespace-nowrap">{p.cell_number || "—"}</td>
            <td className="py-1 px-1 whitespace-nowrap">
              {p.guardian_last_name || p.guardian_first_name
                ? `${p.guardian_last_name || ""}${p.guardian_last_name && p.guardian_first_name ? ", " : ""}${p.guardian_first_name || ""}`
                : "—"}
            </td>
            <td className="py-1 px-1 whitespace-nowrap">{p.guardian_phone || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PrintDepthChart({ players, coaches = {} }) {
  // Group by position → depth
  const grouped = {};
  for (const p of players) {
    const pos = p.position || "—";
    if (!grouped[pos]) grouped[pos] = { 1: [], 2: [], 3: [] };
    const d = p.depth >= 1 && p.depth <= 3 ? p.depth : 1;
    grouped[pos][d].push(p);
  }
  const positions = Object.keys(grouped);

  return (
    <div className="space-y-4">
      {positions.map((pos) => {
        const counts = grouped[pos];
        const coach = coaches[pos];
        return (
          <div key={pos} className="border border-gray-400" data-testid={`print-depth-${pos}`}>
            <div className="bg-gray-100 border-b border-gray-400 px-3 py-1 text-sm font-bold uppercase tracking-[0.2em] flex items-center justify-between">
              <span>{pos}</span>
              {coach && (
                <span className="text-xs font-normal normal-case tracking-normal text-gray-700">
                  Coach: <span className="font-semibold">{coach.name}</span>
                </span>
              )}
            </div>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-400 bg-gray-50">
                  <th className="text-left py-1 px-2 w-16">Depth</th>
                  <th className="text-left py-1 px-2">Player(s)</th>
                  <th className="text-center py-1 px-2 w-14">AD</th>
                  <th className="text-center py-1 px-2 w-14">Play</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3].map((d) => {
                  const rows = counts[d];
                  if (rows.length === 0) {
                    return (
                      <tr key={d} className="border-b border-gray-300">
                        <td className="py-1 px-2 font-bold">{d}{d === 1 ? "st" : d === 2 ? "nd" : "rd"}</td>
                        <td className="py-1 px-2 italic text-gray-400">— open —</td>
                        <td></td>
                        <td></td>
                      </tr>
                    );
                  }
                  return rows.map((p, idx) => (
                    <tr key={p.id} className="border-b border-gray-300">
                      <td className="py-1 px-2 font-bold">
                        {idx === 0 ? `${d}${d === 1 ? "st" : d === 2 ? "nd" : "rd"}` : ""}
                      </td>
                      <td className="py-1 px-2 font-semibold">{p.last_name}, {p.first_name}</td>
                      <td className="py-1 px-2 text-center">{p.ad_cleared ? "✓" : "✗"}</td>
                      <td className="py-1 px-2 text-center">{p.coach_cleared ? "✓" : "✗"}</td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
